import { z } from "zod";

/**
 * Shared DTO cho Admin AI write kill-switch dashboard (US-AIF.P0-0245 · @UC9417).
 *
 * GOM 3 tầng kill-switch AI ghi tiền (đã tồn tại riêng lẻ) về MỘT trạng thái đọc-thật (DP-5):
 *  1. env `AI_ENABLED` (config, READ-ONLY — đổi cần deploy). `envAiEnabled` phản ánh `loadConfig()`.
 *  2. feature flag `ai_chat_t1_write` (DB — toggle được ở /flags, OPS/SUPER_ADMIN). `flag.enabled` +
 *     `flag.rolloutPercent` đọc từ FeatureFlagsService (DB). `flag.exists=false` nếu chưa seed row
 *     (fail-closed nghiệp vụ: vắng ⇒ coi như OFF).
 *  3. per-group `ai_enabled` (jsonb `Group.settings.ai_enabled`). `groups.total` = số nhóm; `groups.
 *     enabledCount` = số nhóm có override `ai_enabled === true` TƯỜNG MINH (khớp cổng ghi tiền
 *     FAIL-CLOSED `isAiWriteEnabled`).
 *
 * BẤT BIẾN (DP-5 — KHÔNG nói dối trạng thái):
 * - `writeGloballyOn` = `envAiEnabled && flag.enabled`. Env OFF ⇒ AI KHÔNG ghi bất kể flag/nhóm.
 *   Flag OFF (hoặc vắng) ⇒ write-intent bị chặn dù env ON. CẢ HAI phải ON mới có thể ghi tiền.
 * - DTO chỉ mang TRẠNG THÁI cờ (boolean/số) — KHÔNG secret/token/PII (C2/C3). Không mang key API.
 * - Endpoint READ (mọi vai admin). Task này KHÔNG toggle — toggle flag vẫn ở /flags (SUPER_ADMIN/OPS),
 *   toggle nhóm vẫn ở GroupDetail. Đây là surface GOM + link, không cơ chế mới.
 */

/** Trạng thái feature flag `ai_chat_t1_write` đọc từ DB (FeatureFlagsService). */
export const aiWriteStatusFlagSchema = z
  .object({
    /** Key flag (hằng — luôn `ai_chat_t1_write`). Echo để UI/consumer khỏi hardcode. */
    key: z.string(),
    /** Row flag có tồn tại trong DB không. false ⇒ chưa seed ⇒ coi như OFF (fail-closed nghiệp vụ). */
    exists: z.boolean(),
    /** enabled từ DB (false nếu vắng row). */
    enabled: z.boolean(),
    /** rollout % [0,100] từ DB (0 nếu vắng row). */
    rolloutPercent: z.number().int().min(0).max(100),
  })
  .strict();

export type AiWriteStatusFlag = z.infer<typeof aiWriteStatusFlagSchema>;

/** Tổng hợp per-group `ai_enabled` override. */
export const aiWriteStatusGroupsSchema = z
  .object({
    /** Tổng số nhóm (mọi trạng thái). */
    total: z.number().int().min(0),
    /** Số nhóm có override `ai_enabled === true` TƯỜNG MINH (cổng ghi tiền fail-closed). */
    enabledCount: z.number().int().min(0),
  })
  .strict();

export type AiWriteStatusGroups = z.infer<typeof aiWriteStatusGroupsSchema>;

/** Response GET /v1/admin/ai/write-status — aggregate 3 tầng kill-switch AI ghi tiền. */
export const adminAiWriteStatusResponseSchema = z
  .object({
    /** env `AI_ENABLED` (READ-ONLY — đổi cần deploy). Đọc từ `loadConfig()`. */
    envAiEnabled: z.boolean(),
    /**
     * US-AI-0294 (@UC9417) — vế DB của tầng 1: `system_settings.ai_enabled_runtime`. Admin chỉnh
     * được LÚC CHẠY (không deploy). Phản ánh giá trị THẬT trong DB kể cả khi env đang chặn — admin
     * phải phân biệt được "env tắt" với "runtime tắt" (DP-5 không nói dối).
     */
    runtimeAiEnabled: z.boolean(),
    /** Tầng 1 hiệu lực = `envAiEnabled && runtimeAiEnabled` (env là TRẦN CỨNG — PO chốt 21/07). */
    effectiveAiEnabled: z.boolean(),
    flag: aiWriteStatusFlagSchema,
    groups: aiWriteStatusGroupsSchema,
    /** Tổng: AI CÓ THỂ ghi tiền toàn cục không = `effectiveAiEnabled && flag.enabled`. */
    writeGloballyOn: z.boolean(),
  })
  .strict();

export type AdminAiWriteStatusResponse = z.infer<typeof adminAiWriteStatusResponseSchema>;

/** Body PATCH /v1/admin/ai/runtime-enabled — bật/tắt tầng 1 lúc chạy (US-AI-0294 · @UC9417). */
export const adminAiRuntimeEnabledRequestSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();

export type AdminAiRuntimeEnabledRequest = z.infer<typeof adminAiRuntimeEnabledRequestSchema>;

/**
 * Code lỗi 422 khi admin cố BẬT runtime trong lúc env `AI_ENABLED=false`.
 * Env là trần cứng ⇒ bật ở DB sẽ vô nghĩa; trả lỗi RÕ thay vì ghi âm thầm rồi vẫn tắt (DP-5).
 */
export const AI_ENV_KILL_SWITCH_OFF = "AI_ENV_KILL_SWITCH_OFF";

/**
 * US-AI-0294 (@UC9417) — 1 nhóm đang BẬT AI (tầng 3), phục vụ panel "Cấu hình AI".
 * PO chốt 21/07: màn hình CHỈ liệt kê nhóm đang bật (+ ô tìm để bật thêm) — KHÔNG list toàn bộ
 * nhóm, tránh trang nặng khi số nhóm lớn.
 */
export const aiEnabledGroupItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .strict();

export type AiEnabledGroupItem = z.infer<typeof aiEnabledGroupItemSchema>;

/** Response GET /v1/admin/ai/enabled-groups — danh sách nhóm có `ai_enabled === true` tường minh. */
export const adminAiEnabledGroupsResponseSchema = z
  .object({
    data: z.array(aiEnabledGroupItemSchema),
    /** Tổng số nhóm đang bật (có thể > data.length khi bị cắt theo `limit`). */
    total: z.number().int().min(0),
  })
  .strict();

export type AdminAiEnabledGroupsResponse = z.infer<typeof adminAiEnabledGroupsResponseSchema>;
